import { useId, useState } from 'react';
import {
  Alert, Box, Button, FormControl, FormHelperText, FormLabel,
  IconButton, Input, Stack, Tooltip, Typography,
} from '@mui/joy';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { proposalsApi } from '../../lib/proposalsApi';
import { proposalSaveError } from '../../lib/proposalPresentationDraft';
import type { ProposalDetail } from '../../types/proposals';

export default function ProposalNumberEditor({ proposal }: { proposal: ProposalDetail }) {
  const queryClient = useQueryClient();
  const inputId = useId();
  const [draft, setDraft] = useState<{ base: string; value: string } | null>(null);
  const number = draft?.base === proposal.proposal_number ? draft.value : proposal.proposal_number;
  const [nextNumber, setNextNumber] = useState('');
  const locked = proposal.status !== 'draft' || !!proposal.sent_at || !!proposal.acceptance_date;
  const mutation = useMutation({
    mutationKey: ['proposal-editor', String(proposal.id)],
    mutationFn: () => proposalsApi.renumber(proposal.id, number.trim(), proposal.proposal_number),
    onSuccess: (result) => {
      setDraft(null);
      setNextNumber(result.next_number);
      queryClient.setQueryData(['proposal', String(proposal.id)], result.proposal);
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
  const match = number.trim().match(/^([A-Za-z0-9._-]*?)([0-9]+)$/);
  const valid = !!match && number.trim().length <= 50 && Number(match[2]) > 0 && Number(match[2]) < 999999999999;
  const suggestedNext = valid ? `${match[1]}${String(Number(match[2]) + 1).padStart(match[2].length, '0')}` : '';
  const changed = number.trim() !== proposal.proposal_number;

  return (
    <Box sx={{ p: 2, borderRadius: 'md', bgcolor: 'background.level1' }}>
      <Stack spacing={1.5}>
        <FormControl disabled={locked || mutation.isPending} error={mutation.isError}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <FormLabel htmlFor={inputId}>Proposal number</FormLabel>
            <Tooltip title="Changing this number also sets the numbering format for future Retail, Insurance and Repair proposals in your organization. Existing proposals keep their numbers. Higher numbers already reached in this sequence are never reused. A custom prefix stays in use until changed, including in a new year.">
              <IconButton size="sm" variant="plain" aria-label="About proposal numbering">
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Input
              value={number}
              onChange={(event) => {
                setDraft({ base: proposal.proposal_number, value: event.target.value });
                mutation.reset();
                setNextNumber('');
              }}
              slotProps={{ input: { id: inputId, maxLength: 50 } }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {!locked && (
              <Button loading={mutation.isPending} disabled={!changed || !valid} onClick={() => mutation.mutate()}>
                Update proposal number
              </Button>
            )}
          </Stack>
          <FormHelperText>
            {locked
              ? 'Issued proposals keep their original number. Create a revision to change it.'
              : 'End with a number, for example P-2026-0500. This change saves separately from the rest of the proposal.'}
          </FormHelperText>
        </FormControl>
        {!locked && changed && suggestedNext && (
          <Typography level="body-sm">
            Future proposals start at {suggestedNext}, or the next higher number if this sequence has
            already passed it. Applies to your entire organization.
          </Typography>
        )}
        {mutation.isError && <Alert color="danger" role="alert">{proposalSaveError(mutation.error)}</Alert>}
        {mutation.isSuccess && nextNumber && (
          <Alert color="success" role="status">Proposal number saved. Next proposal number: {nextNumber}.</Alert>
        )}
      </Stack>
    </Box>
  );
}
